import axios from "axios";
import tokenManager from "../utils/tokenManager";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

class ApiService {
  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      timeout: 30000,
      withCredentials: true, // Enable cookies
    });

    this.setupInterceptors();
  }

  setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      async (config) => {
        if (tokenManager.shouldRefreshToken()) {
          try {
            await tokenManager.refreshAccessToken();
          } catch (error) {
            console.warn("Pre-request token refresh failed:", error);
          }
        }
        let token = tokenManager.getAccessToken();

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          // Don't proceed if we can't get a token for protected routes
          if (!config.url.includes("/auth/")) {
            console.warn("No access token available for protected route");
          }
        }

        // Add CSRF token for state-changing requests
        if (
          ["POST", "PUT", "DELETE", "PATCH"].includes(
            config.method.toUpperCase(),
          )
        ) {
          const csrfToken = tokenManager.getCSRFToken();
          if (csrfToken) {
            config.headers["X-CSRF-Token"] = csrfToken;
          }
        }

        // Add fingerprint
        const fingerprint = await tokenManager.getFingerprint();
        if (fingerprint) {
          config.headers["X-Fingerprint"] = fingerprint;
        }

        // Set content type
        if (!(config.data instanceof FormData)) {
          config.headers["Content-Type"] = "application/json";
        }

        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response.data,
      async (error) => {
        const originalRequest = error.config;

        // Handle token expiration
        if (
          error.response?.status === 401 &&
          error.response?.data?.code === "TOKEN_EXPIRED" &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;

          try {
            const newToken = await tokenManager.refreshAccessToken();
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            if (refreshError.response?.status === 401) {
              tokenManager.clearCurrentSession();
              window.location.href = "/login?reason=session_expired";
            }

            return Promise.reject(refreshError);
          }
        }

        // Handle session timeout
        if (error.response?.data?.code === "SESSION_TIMEOUT") {
          tokenManager.clearCurrentSession();
        }

        // Handle account lockout
        if (error.response?.data?.code === "ACCOUNT_LOCKED") {
          window.dispatchEvent(
            new CustomEvent("accountLocked", {
              detail: { message: error.response.data.message },
            }),
          );
        }

        return Promise.reject(error);
      },
    );
  }

  // Auth endpoints with enhanced security
  async register(userData) {
    try {
      const response = await this.client.post("/auth/register", userData);

      if (response.success) {
        tokenManager.setTokens(
          response.accessToken,
          response.refreshToken,
          response.csrfToken,
        );

        if (response.fingerprint) {
          tokenManager.setFingerprint(response.fingerprint);
        }

        if (response.user) {
          tokenManager.setUser(response.user);
        }
      }

      return response;
    } catch (error) {
      console.error("Register API error:", error);
      throw error;
    }
  }

  async login(email, password, remember = false) {
    const fingerprint = await tokenManager.getFingerprint();

    const response = await this.client.post("/auth/login", {
      email,
      password,
      remember,
      fingerprint,
    });

    if (response.success) {
      tokenManager.setTokens(
        response.accessToken,
        response.refreshToken,
        response.csrfToken,
      );

      if (response.fingerprint) {
        tokenManager.setFingerprint(response.fingerprint);
      }

      if (response.user) {
        tokenManager.setUser(response.user);
      }
    }

    return response;
  }

  async logout(allDevices = false) {
    const refreshToken = tokenManager.getRefreshToken();

    try {
      await this.client.post("/auth/logout", {
        refreshToken,
        allDevices,
      });
    } finally {
      if (allDevices) {
        tokenManager.clearAll();
      } else {
        tokenManager.clearCurrentSession();
      }
    }
  }

  async getSessions() {
    return this.client.get("/auth/sessions");
  }

  async revokeSession(sessionId) {
    return this.client.delete(`/auth/sessions/${sessionId}`);
  }

  async getMe() {
    return this.client.get("/auth/me");
  }

  async forgotPassword(email) {
    return this.client.post("/auth/forgot-password", { email });
  }

  // Onboarding endpoints
  async updateCompanyDetails(companyData) {
    return this.client.put("/onboarding/company", companyData);
  }

  async inviteTeamMembers(emails) {
    return this.client.post("/onboarding/invite-team", { emails });
  }

  async uploadAsset(file) {
    const formData = new FormData();
    formData.append("file", file);
    return this.client.post("/onboarding/upload-assets", formData);
  }

  async updateWorkspaceUrl(workspaceUrl) {
    return this.client.put("/onboarding/workspace-url", { workspaceUrl });
  }

  async validateInvitation(token) {
    return this.client.get(`/auth/invitation/${token}`);
  }

  async acceptInvitation(data) {
    return this.client.post("/auth/accept-invitation", data);
  }

  // Team management
  async getTeamMembers() {
    return this.client.get("/team/members");
  }

  async getLicenseSummary() {
    return this.client.get("/licenses/summary");
  }

  async updateUserProductAccess(
    userId,
    { hasOctopushXAccess, hasRecordingStudioAccess },
  ) {
    return this.client.put(`/team/member/${userId}/access`, {
      hasOctopushXAccess,
      hasRecordingStudioAccess,
    });
  }
// ─── Demo Login ───────────────────────────────────────────────────────────
  // Add this method to the ApiService class in api.js

  async demoLogin({ name, email, companyName }) {
    const response = await this.client.post("/auth/demo-login", {
      name,
      email,
      companyName,
    });

    if (response.success) {
      tokenManager.setTokens(
        response.accessToken,
        response.refreshToken,
        response.csrfToken,
      );

      if (response.fingerprint) {
        tokenManager.setFingerprint(response.fingerprint);
      }

      if (response.user) {
        tokenManager.setUser(response.user);
      }
    }

    return response;
  }
  // Replace the existing inviteTeamMember with this richer version
  async inviteTeamMember(email, role = "member", grants = {}) {
    return this.client.post("/team/invite", {
      email,
      role,
      grants: {
        octopushX: !!grants.octopushX,
        recordingStudio: !!grants.recordingStudio,
      },
    });
  }

  async updateUserRole(userId, role) {
    return this.client.put(`/team/member/${userId}/role`, { role });
  }

  async removeUser(userId) {
    return this.client.delete(`/team/member/${userId}`);
  }

  async resendInvitation(invitationId) {
    return this.client.post(`/team/invitation/${invitationId}/resend`);
  }

  async cancelInvitation(invitationId) {
    return this.client.delete(`/team/invitation/${invitationId}`);
  }

  // Profile management
  async updatePersonalDetails(data) {
    return this.client.put("/auth/update-profile", data);
  }

  async uploadProfileImage(formData) {
    return this.client.post("/auth/upload-profile-image", formData);
  }

  // Company endpoints
  async updateCompanyData(data) {
    return this.client.put("/company/update", data);
  }

  async uploadCompanyLogo(formData) {
    return this.client.post("/company/upload-logo", formData);
  }

  // Recording Studio endpoints
  async getRecordingStudioLicense() {
    return this.client.get("/recording-studio/license");
  }

  async getVoices(languageCode, extraParams = {}) {
    return this.client.get("/recording-studio/voices", {
      params: {
        languageCode,
        ...extraParams,
      },
    });
  }

  async createVoiceProject(projectData) {
    return this.client.post("/recording-studio/projects", projectData);
  }

  async getVoiceProjects(filters = {}) {
    return this.client.get("/recording-studio/projects", { params: filters });
  }

  async getVoiceProject(projectId) {
    return this.client.get(`/recording-studio/projects/${projectId}`);
  }

  async updateVoiceProject(projectId, data) {
    return this.client.put(`/recording-studio/projects/${projectId}`, data);
  }

  async deleteVoiceProject(projectId) {
    return this.client.delete(`/recording-studio/projects/${projectId}`);
  }

  async addVoiceBlock(projectId, blockData) {
    return this.client.post(
      `/recording-studio/projects/${projectId}/blocks`,
      blockData,
    );
  }

  async updateVoiceBlock(projectId, blockId, data) {
    return this.client.put(
      `/recording-studio/projects/${projectId}/blocks/${blockId}`,
      data,
    );
  }

  async deleteVoiceBlock(projectId, blockId) {
    return this.client.delete(
      `/recording-studio/projects/${projectId}/blocks/${blockId}`,
    );
  }

  /**
   * Layout-only patch: updates timelineStart / duration / orderIndex
   * WITHOUT resetting audioUrl or synthesis status.
   * Use this for drag-move, drag-resize, and sidebar reorder.
   */
  async patchVoiceBlockLayout(projectId, blockId, data) {
    return this.client.patch(
      `/recording-studio/projects/${projectId}/blocks/${blockId}/layout`,
      data,
    );
  }

  async synthesizeVoiceBlock(projectId, blockId) {
    return this.client.post(
      `/recording-studio/projects/${projectId}/blocks/${blockId}/synthesize`,
    );
  }

  async renderVoiceProject(projectId) {
    return this.client.post(`/recording-studio/projects/${projectId}/render`);
  }

  async getMusicLibrary(filters = {}) {
    return this.client.get("/recording-studio/music", { params: filters });
  }
  // In api.js
  async getSupportedFormats() {
    return this.client.get("/recording-studio/supported-formats");
  }

  async isolateSpeech(formData) {
    return this.client.post("/speech-isolation", formData, {
      timeout: 5 * 60 * 1000,
    });
  }

  // AI Script Generator — streams via SSE, so we use fetch directly
  async generateScript(data, { onDelta, onDone, onError, signal } = {}) {
    // Ensure token is fresh
    if (tokenManager.shouldRefreshToken()) {
      try {
        await tokenManager.refreshAccessToken();
      } catch (e) {
        console.warn("Pre-request token refresh failed:", e);
      }
    }

    const token = tokenManager.getAccessToken();
    const csrfToken = tokenManager.getCSRFToken();
    const fingerprint = await tokenManager.getFingerprint();

    const response = await fetch(
      `${API_URL}/recording-studio/generate-script`,
      {
        method: "POST",
        credentials: "include",
        signal,
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
          ...(csrfToken && { "X-CSRF-Token": csrfToken }),
          ...(fingerprint && { "X-Fingerprint": fingerprint }),
        },
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(
        `Script generation failed (${response.status})${text ? ": " + text : ""}`,
      );
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split("\n");
      buffer = lines.pop();

      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const payload = line.slice(6).trim();
        if (!payload) continue;
        try {
          const parsed = JSON.parse(payload);
          if (parsed.type === "delta") onDelta?.(parsed.text);
          else if (parsed.type === "done") onDone?.();
          else if (parsed.type === "error")
            onError?.(parsed.message || "Generation failed");
        } catch {
          // ignore malformed chunks
        }
      }
    }
  }
}

export default new ApiService();
